import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Fab } from '@mui/material';

const MARGIN = 8;
const DRAG_THRESHOLD = 5; // px of movement before a press counts as a drag, not a click

const clampToViewport = (x, y, size) => {
  const maxX = Math.max(MARGIN, window.innerWidth - size - MARGIN);
  const maxY = Math.max(MARGIN, window.innerHeight - size - MARGIN);
  return { x: Math.min(Math.max(x, MARGIN), maxX), y: Math.min(Math.max(y, MARGIN), maxY) };
};

const pointer = (e) => (e.touches && e.touches[0] ? e.touches[0] : e);

/**
 * A Fab the user can drag anywhere on screen (persisted per `storageKey`),
 * instead of being pinned to one corner. Falls back to the caller's `sx`
 * (bottom/right) positioning until it's been dragged at least once.
 */
const DraggableFab = React.forwardRef(function DraggableFab(
  { storageKey, size = 56, onClick, sx, children, ...rest }, forwardedRef,
) {
  const [pos, setPos] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  });
  const elRef = useRef(null);
  const drag = useRef({ active: false, moved: false, startX: 0, startY: 0, origX: 0, origY: 0 });

  const setRefs = useCallback((node) => {
    elRef.current = node;
    if (typeof forwardedRef === 'function') forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  }, [forwardedRef]);

  useEffect(() => {
    const onResize = () => {
      setPos((p) => {
        if (!p) return p;
        const clamped = clampToViewport(p.x, p.y, size);
        try { localStorage.setItem(storageKey, JSON.stringify(clamped)); } catch (e) { /* ignore */ }
        return clamped;
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [size, storageKey]);

  const onMove = useCallback((e) => {
    if (!drag.current.active) return;
    e.preventDefault();
    const { clientX, clientY } = pointer(e);
    const dx = clientX - drag.current.startX;
    const dy = clientY - drag.current.startY;
    if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) drag.current.moved = true;
    setPos(clampToViewport(drag.current.origX + dx, drag.current.origY + dy, size));
  }, [size]);

  const onUp = useCallback(() => {
    if (!drag.current.active) return;
    drag.current.active = false;
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mouseup', onUp);
    window.removeEventListener('touchmove', onMove);
    window.removeEventListener('touchend', onUp);
    if (drag.current.moved) {
      setPos((p) => {
        if (p) { try { localStorage.setItem(storageKey, JSON.stringify(p)); } catch (e) { /* ignore */ } }
        return p;
      });
    }
  }, [onMove, storageKey]);

  const onDown = (e) => {
    const { clientX, clientY } = pointer(e);
    const rect = elRef.current.getBoundingClientRect();
    drag.current = { active: true, moved: false, startX: clientX, startY: clientY, origX: rect.left, origY: rect.top };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
  };

  const handleClick = (e) => {
    if (drag.current.moved) {
      drag.current.moved = false;
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onClick?.(e);
  };

  const posStyle = pos ? { position: 'fixed', left: pos.x, top: pos.y, right: 'auto', bottom: 'auto' } : {};

  return (
    <Fab
      ref={setRefs}
      onMouseDown={onDown}
      onTouchStart={onDown}
      onClick={handleClick}
      sx={{ ...sx, ...posStyle, cursor: 'grab', '&:active': { cursor: 'grabbing' }, touchAction: 'none' }}
      {...rest}
    >
      {children}
    </Fab>
  );
});

export default DraggableFab;
